import chalk from "chalk";

/**
 * Shortens a Solana address for display purposes
 * @param address - The full Solana address
 * @param prefixLength - Number of characters to show at the start (default: 8)
 * @param suffixLength - Number of characters to show at the end (default: 8)
 * @returns Shortened address in format "12345678...87654321"
 */
export function shortenAddress(
  address: string,
  prefixLength: number = 4,
  suffixLength: number = 4
): string {
  if (address.length <= prefixLength + suffixLength + 3) {
    return address;
  }
  return `${address.slice(0, prefixLength)}...${address.slice(-suffixLength)}`;
}

/**
 * Creates a Solscan explorer URL for a given address and network
 * @param address - The Solana address
 * @param network - The network (mainnet, devnet, localnet)
 * @returns Full Solscan URL
 */
export function getExplorerUrl(address: string, network: string = "mainnet"): string {
  const baseUrl = "https://explorer.solana.com";
  let networkParam = network === "mainnet" ? "" : `?cluster=${network}`;
  if (network === "localnet") {
    networkParam = `?cluster=custom&customUrl=http://localhost:8899`;
  }
  return `${baseUrl}/address/${address}${networkParam}`;
}

/**
 * Creates a Solana Explorer URL for a given transaction signature and network
 * @param txSignature - The transaction signature
 * @param network - The network (mainnet, devnet, localnet)
 * @returns Full Solana Explorer URL for transaction
 */
export function getTransactionExplorerUrl(
  txSignature: string,
  network: string = "mainnet"
): string {
  const baseUrl = "https://explorer.solana.com";
  const networkParam = network === "mainnet" ? "" : `?cluster=${network}`;
  return `${baseUrl}/tx/${txSignature}${networkParam}`;
}

/**
 * Formats an address with a clickable link to Solscan
 * @param address - The Solana address
 * @param network - The network (mainnet, devnet, localnet)
 * @param displayText - Custom text for the link (default: "Solscan")
 * @returns Formatted string with shortened address and clickable link
 */
export function formatAddressWithLink(address: string, network: string = "mainnet"): string {
  const shortened = shortenAddress(address);
  const explorerUrl = getExplorerUrl(address, network);

  // Check if terminal supports hyperlinks
  const supportsHyperlinks = process.env.TERM_PROGRAM || process.stdout.isTTY;

  if (supportsHyperlinks) {
    return `\u001b]8;;${explorerUrl}\u0007${chalk.blue.underline(shortened)}\u001b]8;;\u0007`;
  } else {
    return `${chalk.gray(shortened)} ${chalk.dim("(Solscan: " + explorerUrl + ")")}`;
  }
}

/**
 * Creates a simple address display without links (for copy-paste scenarios)
 * @param address - The Solana address
 * @param shouldShorten - Whether to shorten the address (default: true)
 * @returns Formatted address string
 */
export function formatAddressSimple(address: string, shouldShorten: boolean = true): string {
  if (shouldShorten) {
    return chalk.gray(shortenAddress(address));
  }
  return chalk.gray(address);
}

/**
 * Validates if a string looks like a valid Solana address
 * @param address - The address to validate
 * @returns True if the address format is valid
 */
export function isValidSolanaAddress(address: string): boolean {
  // Basic validation: Solana addresses are base58 encoded and typically 44 characters
  const base58Regex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
  return base58Regex.test(address);
}

/**
 * Formats a transaction signature with a clickable link to Solana Explorer
 * @param txSignature - The transaction signature
 * @param network - The network (mainnet, devnet, localnet)
 * @returns Formatted string with shortened signature and clickable link
 */
export function formatTransactionWithLink(
  txSignature: string,
  network: string = "mainnet"
): string {
  const shortened = shortenAddress(txSignature, 8, 8); // Use longer prefix/suffix for transactions
  const explorerUrl = getTransactionExplorerUrl(txSignature, network);

  // Check if terminal supports hyperlinks
  const supportsHyperlinks = process.env.TERM_PROGRAM || process.stdout.isTTY;

  if (supportsHyperlinks) {
    return `\u001b]8;;${explorerUrl}\u0007${chalk.green.underline(shortened)}\u001b]8;;\u0007`;
  } else {
    return `${chalk.green(shortened)} ${chalk.dim("(Explorer: " + explorerUrl + ")")}`;
  }
}

/**
 * Creates a simple transaction signature display without links
 * @param txSignature - The transaction signature
 * @param shouldShorten - Whether to shorten the signature (default: true)
 * @returns Formatted transaction signature string
 */
export function formatTransactionSimple(
  txSignature: string,
  shouldShorten: boolean = true
): string {
  if (shouldShorten) {
    return chalk.green(shortenAddress(txSignature, 8, 8));
  }
  return chalk.green(txSignature);
}

/**
 * Copies an address to clipboard (if running in supported environment)
 * @param address - The address to copy
 * @returns Promise resolving to success status
 */
export async function copyAddressToClipboard(address: string): Promise<boolean> {
  try {
    // For Node.js environments, we'd need a clipboard library
    // For now, just return false as this would require additional dependencies
    console.log(chalk.dim(`💡 Address ready to copy: ${address}`));
    return false;
  } catch {
    return false;
  }
}
